import { Button } from "../../../ui/index.ts";
import type { ContactStepValues, PersonalStepValues } from "../schemas/create-employee.schema.ts";

import styles from "./review-step.module.css";

export type ReviewStepProps = {
  personal: PersonalStepValues;
  contact: ContactStepValues;
  loading: boolean;
  onBack: () => void;
  onSubmit: () => void;
  hideFooter?: boolean;
};

function capitalizeWord(s: string): string {
  if (!s) return s;
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function displayMiddle(middle: string | undefined): string {
  const t = middle?.trim();
  return t && t.length > 0 ? t : "-";
}

function displayPassport(passport: string | undefined): string {
  const t = passport?.trim();
  return t && t.length > 0 ? t : "-";
}

export function ReviewStep({
  personal,
  contact,
  loading,
  onBack,
  onSubmit,
  hideFooter = false,
}: ReviewStepProps) {
  return (
    <div className={styles.sections}>
      <p className={`body-sm ${styles.lead}`}>Review your details before submission.</p>
      <hr className={styles.separator} />
      <section aria-labelledby="create-employee-review-personal">
        <h3 id="create-employee-review-personal" className={`heading-xs ${styles.sectionTitle}`}>
          Personal Details
        </h3>
        <div className={styles.grid}>
          <div className={styles.pair}>
            <p className={`body-sm ${styles.term}`}>First Name</p>
            <p className={styles.desc}>{personal.firstName}</p>
          </div>
          <div className={styles.pair}>
            <p className={`body-sm ${styles.term}`}>Middle Name</p>
            <p className={styles.desc}>{displayMiddle(personal.middleName)}</p>
          </div>
          <div className={styles.pair}>
            <p className={`body-sm ${styles.term}`}>Last Name</p>
            <p className={styles.desc}>{personal.lastName}</p>
          </div>
          <div className={styles.pair}>
            <p className={`body-sm ${styles.term}`}>Personal Email Address</p>
            <p className={styles.desc}>{personal.personalEmail}</p>
          </div>
          <div className={styles.pair}>
            <p className={`body-sm ${styles.term}`}>Gender</p>
            <p className={styles.desc}>{capitalizeWord(personal.gender)}</p>
          </div>
          <div className={styles.pair}>
            <p className={`body-sm ${styles.term}`}>Blood Group</p>
            <p className={styles.desc}>{personal.bloodGroup}</p>
          </div>
          <div className={styles.pair}>
            <p className={`body-sm ${styles.term}`}>Date of Joining</p>
            <p className={styles.desc}>{personal.joiningDate}</p>
          </div>
          <div className={styles.pair}>
            <p className={`body-sm ${styles.term}`}>Marital Status</p>
            <p className={styles.desc}>
              {personal.maritalStatus}
              {personal.maritalStatus === "Married" && personal.anniversaryDate
                ? ` ${personal.anniversaryDate}`
                : ""}
            </p>
          </div>
          <div className={styles.pair}>
            <p className={`body-sm ${styles.term}`}>Date of Birth</p>
            <p className={styles.desc}>{personal.dob}</p>
          </div>
          <div className={styles.pair}>
            <p className={`body-sm ${styles.term}`}>Aadhar Number</p>
            <p className={styles.desc}>{personal.aadharNumber}</p>
          </div>
          <div className={styles.pair}>
            <p className={`body-sm ${styles.term}`}>PAN Card Number</p>
            <p className={styles.desc}>{personal.panCardNumber}</p>
          </div>
          <div className={styles.pair}>
            <p className={`body-sm ${styles.term}`}>Passport Number</p>
            <p className={styles.desc}>{displayPassport(personal.passportNumber)}</p>
          </div>
          {personal.maritalStatus === "Married" ? (
            <>
              <div className={styles.pair}>
                <p className={`body-sm ${styles.term}`}>Spouse Name</p>
                <p className={styles.desc}>{personal.spouseName}</p>
              </div>
              <div className={styles.pair}>
                <p className={`body-sm ${styles.term}`}>Spouse Date of Birth</p>
                <p className={styles.desc}>{personal.spouseDob}</p>
              </div>
              <div className={styles.pair}>
                <p className={`body-sm ${styles.term}`}>Do you have a child below 18 years of age?</p>
                <p className={styles.desc}>{capitalizeWord(personal.hasChild ?? "")}</p>
              </div>
              {personal.hasChild === "yes" ? (
                <>
                  <div className={styles.pair}>
                    <p className={`body-sm ${styles.term}`}>Child Name</p>
                    <p className={styles.desc}>{personal.childName}</p>
                  </div>
                  <div className={styles.pair}>
                    <p className={`body-sm ${styles.term}`}>Child Date of Birth</p>
                    <p className={styles.desc}>{personal.childDob}</p>
                  </div>
                  <div className={styles.pair}>
                    <p className={`body-sm ${styles.term}`}>Child Gender</p>
                    <p className={styles.desc}>
                      {capitalizeWord(personal.childGender ?? "")}
                    </p>
                  </div>
                </>
              ) : null}
            </>
          ) : null}
        </div>
      </section>

      <hr className={styles.separator} />
      <section aria-labelledby="create-employee-review-contact">
        <h3 id="create-employee-review-contact" className={`heading-xs ${styles.sectionTitle}`}>
          Contact Details
        </h3>
        <div className={styles.grid}>
          <div className={styles.pair}>
            <p className={`body-sm ${styles.term}`}>Contact Number</p>
            <p className={styles.desc}>{contact.phoneNumber}</p>
          </div>
          <div className={styles.pair}>
            <p className={`body-sm ${styles.term}`}>Current Residential Address</p>
            <p className={styles.desc}>{contact.presentAddress}</p>
          </div>
          <div className={styles.pair}>
            <p className={`body-sm ${styles.term}`}>Permanent Address</p>
            <p className={styles.desc}>{contact.permanentAddress}</p>
          </div>
          <div className={styles.pair}>
            <p className={`body-sm ${styles.term}`}>Emergency Contact Name</p>
            <p className={styles.desc}>{contact.alternateName}</p>
          </div>
          <div className={styles.pair}>
            <p className={`body-sm ${styles.term}`}>Emergency Contact Number</p>
            <p className={styles.desc}>{contact.alternateNumber}</p>
          </div>
          <div className={styles.pair}>
            <p className={`body-sm ${styles.term}`}>Relationship with Emergency Contact Person</p>
            <p className={styles.desc}>{capitalizeWord(contact.relationWithAlternateNo)}</p>
          </div>

        </div>
      </section>

      {hideFooter ? null : (
        <div className={styles.actions}>
          <Button type="button" variant="muted-outline" disabled={loading} onClick={onBack}>
            Previous
          </Button>
          <Button type="button" disabled={loading} onClick={onSubmit}>
            {loading ? "Submitting…" : "Submit"}
          </Button>
        </div>
      )}
    </div>
  );
}
